'use client';

// Third Party
import * as React from 'react';
import * as Sentry from '@sentry/nextjs';

// Utils
import { useMutation } from '@apollo/client/react';
import { SubscribeEmailToNewsletterDocument } from '@gql';

// UI
import { Button } from '@components/button';
import { Spinner } from '@components/spinner';
import { Typography } from '@components/typography';
import { Icon, IconNames } from '@components/icon';

export const NewsletterForm = (): React.JSX.Element => {
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const [subscribeEmailToNewsletter] = useMutation(
    SubscribeEmailToNewsletterDocument
  );

  const onChangeHandler = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(value);
  };

  const onSubmitHandler = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      setSubmitError(null);
      await subscribeEmailToNewsletter({ variables: { email } });
      setEmail('');
      setIsSubscribed(true);
    } catch (error) {
      Sentry.captureException(error);
      setSubmitError('Subscription failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubscribed) {
    return (
      <div className="flex flex-col gap-2">
        <Typography variant="subtitle2" className="text-secondary-light">
          Thanks for subscribing!
        </Typography>
        <Typography variant="body2" className="text-secondary-light">
          Check your inbox for confirmation
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-center justify-end gap-4 lg:space-x-2 lg:flex-row pt-4 lg:pt-0">
      <div className="flex flex-col text-left items-start w-full lg:w-auto whitespace-nowrap">
        <Typography variant="body1" className="text-left text-secondary-main">
          Sign up for our newsletter:
        </Typography>
      </div>
      <div className="flex flex-col w-full">
        <form
          onSubmit={onSubmitHandler}
          className="flex flex-row items-center border border-white rounded-full bg overflow-hidden w-full max-w-md"
        >
          <input
            name="email"
            type="email"
            value={email}
            onChange={onChangeHandler}
            placeholder="name@email.com"
            className="px-4 py-2 bg-transparent text-white placeholder-white outline-none w-full"
            required
          />
          <Button
            type="submit"
            variant="text"
            size="md"
            className="pr-4 py-2 text-white hover:text-secondary-main transition-colors w-20 items-center justify-center"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Spinner className="text-white" />
            ) : (
              <Icon
                name={IconNames.PAPER_PLANE}
                size="lg"
                className="text-white"
              />
            )}
          </Button>

          {/* <!-- real people should not fill this in and expect good things - do not remove this or risk form bot signups--> */}
          <div
            style={{ position: 'absolute', left: '-5000px' }}
            aria-hidden="true"
          >
            <input
              type="text"
              name="b_5b96ebc913818d363151bbe46_2f838d472e"
              tabIndex={-1}
              defaultValue=""
            />
          </div>
        </form>
        {submitError && (
          <Typography
            variant="small"
            component="p"
            className="text-error-light mt-2"
          >
            {submitError}
          </Typography>
        )}
      </div>
    </div>
  );
};
